import { z } from 'zod';

// =============================================================================
// Per-view payload schemas
//
// `VIEW_PREFERENCE_SCHEMAS` is the SINGLE source of truth. Add a new view
// here and everything else — ViewKey type, validation, typed hooks — is
// derived. Forgetting to register elsewhere is a compile error, not a
// silent runtime bug.
//
// Adding a new view:
//   1. Export a Zod schema for its shape.
//   2. Add the mapping in `VIEW_PREFERENCE_SCHEMAS`.
//   (Nothing else to touch. `ViewKey`, `ViewPreferenceFor<K>`, and the
//    `VIEW_KEY` autocomplete enum all flow from the map.)
// =============================================================================

/**
 * Tasks inbox filters (status / show / labels / date range). Mirrors the
 * URL query params the page already uses; we persist only values that
 * should survive a logout. Search terms, pagination, and the selected
 * task id are intentionally excluded.
 */
export const tasksInboxPreferenceSchema = z
  .object({
    status: z.string().optional(),
    show: z.string().optional(),
    labels: z.string().optional(),
    dateFrom: z.string().optional(),
    dateTo: z.string().optional(),
    assignedTo: z.string().optional(),
    assigneeType: z.enum(['user', 'team']).optional(),
  })
  .strict();

export type TasksInboxPreference = z.infer<typeof tasksInboxPreferenceSchema>;

/**
 * Document extractions list filters (urgency / classification / referring
 * provider / health plan / date range). Mirrors the URL query params the
 * page uses; ephemeral state (search term, pagination, sort) is excluded.
 */
export const documentsPreferenceSchema = z
  .object({
    urgency: z.string().optional(),
    classification: z.string().optional(),
    referringProviderName: z.string().optional(),
    healthPlan: z.string().optional(),
    dateFrom: z.string().optional(),
    dateTo: z.string().optional(),
  })
  .strict();

export type DocumentsPreference = z.infer<typeof documentsPreferenceSchema>;

/**
 * Dashboard home layout. The user's dashboard is a grid of tiles, each
 * with a position (x,y), size (w,h), tile type, and per-tile config
 * (e.g. assignee filter, date range). Layout is persisted as a list so
 * stable tile ids survive add/remove without re-keying the grid.
 *
 * Caps: max 30 tiles per dashboard (the 8 KB JSONB cap from
 * `MAX_VIEW_PAYLOAD_BYTES` is a hard upper bound — at ~150 bytes/tile
 * we leave plenty of headroom). Grid is 12 columns wide (`w` ∈ 1..12).
 */
export const dashboardTileSchema = z
  .object({
    /** Stable client-generated id (uuid). Survives add/remove/reorder. */
    id: z.string().min(1).max(64),
    /** Tile type — must be a registered type in the frontend tile registry. */
    type: z.string().min(1).max(64),
    /** Grid column (0..11). */
    x: z.number().int().min(0).max(11),
    /** Grid row (0..). Unbounded — react-grid-layout tracks rows freely. */
    y: z.number().int().min(0).max(200),
    /** Width in columns (1..12). */
    w: z.number().int().min(1).max(12),
    /** Height in rows (1..). */
    h: z.number().int().min(1).max(50),
    /**
     * Per-tile configuration. Loose shape — each tile type owns its
     * own config schema in the frontend. Tiles without config emit
     * `{}` (or undefined → omitted). Cap at 1 KB serialized per tile
     * to keep total payload safely under the 8 KB envelope.
     */
    config: z.record(z.string(), z.unknown()).optional(),
  })
  .strict();

export const dashboardHomePreferenceSchema = z
  .object({
    tiles: z.array(dashboardTileSchema).max(30),
  })
  .strict();

export type DashboardTile = z.infer<typeof dashboardTileSchema>;
export type DashboardHomePreference = z.infer<typeof dashboardHomePreferenceSchema>;

/**
 * Registry of every view_key the server knows how to validate. Keep the
 * value type wide enough (`z.ZodTypeAny`) for `.strict()`-narrowed
 * schemas; keep the KEY type narrow via `as const` so `keyof typeof`
 * gives us a precise literal union downstream.
 */
export const VIEW_PREFERENCE_SCHEMAS = {
  'tasks.inbox': tasksInboxPreferenceSchema,
  'documents.list': documentsPreferenceSchema,
  'dashboard.home': dashboardHomePreferenceSchema,
} as const satisfies Record<string, z.ZodTypeAny>;

/** String-literal union of all registered view_keys. */
export type ViewKey = keyof typeof VIEW_PREFERENCE_SCHEMAS;

/** Precise payload type for a given view_key, inferred from its Zod schema. */
export type ViewPreferenceFor<K extends ViewKey> = z.infer<(typeof VIEW_PREFERENCE_SCHEMAS)[K]>;

/**
 * Autocomplete-friendly constant for call sites that don't want to
 * type string literals. Satisfies `ViewKey`, so adding an entry here
 * without a matching registration in `VIEW_PREFERENCE_SCHEMAS` is a
 * compile error.
 */
export const VIEW_KEY = {
  TASKS_INBOX: 'tasks.inbox',
  DOCUMENTS_LIST: 'documents.list',
  DASHBOARD_HOME: 'dashboard.home',
} as const satisfies Record<string, ViewKey>;

/** Narrows an arbitrary string to a known `ViewKey`. */
export function isKnownViewKey(key: string): key is ViewKey {
  return key in VIEW_PREFERENCE_SCHEMAS;
}

/** Look up the Zod schema for a view_key. Gate with `isKnownViewKey` first. */
export function viewPreferenceSchemaFor<K extends ViewKey>(
  key: K
): (typeof VIEW_PREFERENCE_SCHEMAS)[K] {
  return VIEW_PREFERENCE_SCHEMAS[key];
}

// =============================================================================
// Full-blob response
// =============================================================================

/**
 * Loose container shape — unknown keys from a newer frontend deserialize
 * without rejection. Per-view validation happens at the slice-accessor
 * layer (frontend) and the PATCH boundary (backend); readers filter by
 * known view_keys at consumption time.
 */
export const userPreferencesResponseSchema = z.object({
  preferences: z.record(z.string(), z.record(z.string(), z.unknown())),
});

export type UserPreferencesResponse = z.infer<typeof userPreferencesResponseSchema>;
